'use client';

import { type ComponentProps } from 'react';

import NextLink from 'next/link';

import { defaultLocale } from '.';
import { useLocale } from './context';

type LinkProps = ComponentProps<typeof NextLink>;

/**
 * Localized Link component that automatically prefixes hrefs with the current locale
 * (unless it's the default locale)
 */
export function Link({ href, ...props }: LinkProps) {
  const locale = useLocale();

  // Convert href to string for processing
  const hrefString = typeof href === 'string' ? href : href.pathname || '';

  // Don't modify external links, anchor links, or already localized paths
  if (
    hrefString.startsWith('http') ||
    hrefString.startsWith('#') ||
    hrefString.startsWith('mailto:') ||
    hrefString.startsWith('tel:')
  ) {
    return <NextLink href={href} {...props} />;
  }

  // For default locale (English), no prefix needed
  if (locale === defaultLocale) {
    return <NextLink href={href} {...props} />;
  }

  // For other locales, add the prefix
  const localizedHref =
    typeof href === 'string'
      ? `/${locale}${hrefString.startsWith('/') ? hrefString : `/${hrefString}`}`
      : {
          ...href,
          pathname: `/${locale}${(href.pathname || '/').startsWith('/') ? href.pathname : `/${href.pathname}`}`,
        };

  return <NextLink href={localizedHref} {...props} />;
}

export default Link;
